import { AlertCircle, ArrowDownRight, ArrowUpRight } from "lucide-react";

const StatCard = ({
  Icon,
  iconBgColor,
  iconColor,
  svg,
  title,
  value,
  target,
  unit,
  trend,
  trendLabel,
  color,
  variant = "linear",
  alert,
  statsBadge = null,
  statusText = "",
  isAlert,
  isAlertColor = null,
  onClick = () => { },
  className = "",
  positive,
  positiveIcon,
  stateLable,
  isBadgeColorSame

}) => {
  const LinearProgress = ({ value, max, color = "#10b981", height = 6 }) => {
    const percentage = Math.min((value / max) * 100, 100);
    return (
      <div
        style={{
          width: "100%",
          height,
          backgroundColor: "#f1f5f9",
          borderRadius: height,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: height,
            transition: "width 1s ease",
          }}
        />
      </div>
    );
  };

  const CircularProgress = ({ value, max, size = 48, strokeWidth = 4, color = "#10b981" }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const percentage = Math.min((value / max) * 100, 100);
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
    );
  };

  const TrendBadge = ({ value, label }) => {
    const isPositive = value >= 0;
    const isNeutral = value === 0;
    const color = isNeutral ? "#94a3b8" : isPositive ? "#10b981" : "#ef4444";
    const bgColor = isNeutral ? "#f8fafc" : isPositive ? "#ecfdf5" : "#fef2f2";
    const arrow = isNeutral ? "↓↑" : isPositive ? "↑" : "↓";

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color,
            backgroundColor: bgColor,
            padding: "2px 7px",
            borderRadius: 6,
            letterSpacing: 0.2,
          }}
        >
          {arrow} {Math.abs(value)}%
        </span>
        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{label}</span>
      </div>
    );
  };

  const percentage = Math.min((value / target) * 100, 100);

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: 16,
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        border: alert ? "1.5px solid #fecaca" : "1px solid #f1f5f9",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        cursor: "default",
        flex: 1,
        minWidth: 200,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      onClick={onClick}
      className={`${className}`}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            className={`max-w-max text-2xl p-[5px] rounded-[5px] flex items-center justify-center`}
            style={{ background: iconBgColor, color: iconColor }}
          >
            {Icon ? (
              <Icon className={`w-6 h-6`} />
            ) : svg ? (
              svg
            ) : null}
          </div>

          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#64748b",
              letterSpacing: 0.3,
              textTransform: "uppercase",
            }}
          >
            {title}
          </span>
        </div>

        {variant === "circular" && (
          <CircularProgress value={value} max={target} color={color} size={44} strokeWidth={4} />
        )}
      </div>

      {/* Value */}
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: color ? color : "#0f172a",
                lineHeight: 1,
              }}
            >
              {value}
            </span>
            {unit ? <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>{unit}</span> : null}
          </div>
          <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 500, marginTop: isAlert ? 2 : 8, display: "block", }}>
            {statsBadge ? statsBadge : `of ${target} ${unit} target`}
          </span>
        </div>

        {positive != null && (
          <span
            className={`inline-flex items-center text-[13px] px-3 py-2 rounded-full ${positive
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
              }`}
            style={isBadgeColorSame ? {backgroundColor: iconBgColor, color: iconColor} : {}}
          >

            {
              positiveIcon ? (
                positive ? (
                  <ArrowUpRight className="h-3 w-3 mr-1" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 mr-1" />
                )
              ) : null
            }
            {stateLable}
          </span>
        )}
      </div>

      {/* Progress bar (linear variant) */}
      {variant === "linear" && (
        <div>
          <LinearProgress value={value} max={target} color={color} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 5,
            }}
          >
            <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
              {percentage.toFixed(0)}% complete
            </span>
            <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
              {(target - value).toFixed(value % 1 === 0 ? 0 : 2)} remaining
            </span>
          </div>
        </div>
      )}

      {/* Trend */}
      {(trend !== undefined && trend !== null) ? <TrendBadge value={trend} label={trendLabel} /> : null}

      {isAlert ?
        <div
          className="flex items-center justify-center gap-1.5 bg-[rgba(0,0,0,0.04)] text-[#8a8480] text-[11px] rounded-lg px-2.5 py-2"
          style={
            (isAlertColor === "risk") ? { background: 'rgb(254, 242, 242)', color: 'rgb(220, 38, 38)' } :
              (isAlertColor === "warring") ? { background: 'rgb(255, 251, 235)', color: 'rgb(217, 119, 6)' } :
                (isAlertColor === "ok") ? { background: 'rgb(239, 246, 255)', color: 'rgb(37, 99, 235)' } :
                  (isAlertColor === "best") ? { background: 'rgb(236, 253, 245)', color: 'rgb(5, 150, 105)' } :
                    {}
          }
        >
          <AlertCircle className="w-3 h-3 shrink-0" strokeWidth={2.5} />
          {statusText}
        </div>
        :
        null
      }
    </div>
  );
};

export default StatCard;